import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Boxes, Plus, ArrowRight, Trash2, Loader2, Award, Database } from "lucide-react";
import { listGold, listGoldSources, removeGold } from "../../lib/s3Api";
import { useRole } from "../../lib/role.jsx";
import { Badge } from "../Primitives";
import GoldAggregationStudio from "../GoldAggregationStudio";
import { CuratedList, useCurated } from "./SilverTab";
import GoldPrompt from "./GoldPrompt";
import { LayerChip, btn, num, when } from "../pipeline/Layer";
import { cn } from "../../lib/utils";

function Fact({ m, canEdit, onDelete }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`gold-model-${m.id}`} className={cn("border rounded-sm", m.state === "promoted" ? "border-success/40 bg-success/5" : "border-primary/40 bg-primary/5")}>
      <div className="p-3 flex items-start gap-3">
        <button onClick={() => setOpen(o => !o)} data-testid={`gold-expand-${m.id}`} className="w-6 h-6 shrink-0 flex items-center justify-center rounded-sm hover:bg-surface">
          <ArrowRight size={12} className={cn("transition-transform", open && "rotate-90")} /></button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono-tabular font-medium text-sm">{m.output_table}</span>
            <Badge status={m.state === "promoted" ? "success" : "info"}>{m.state}</Badge>
            <Badge status="info">grain: {m.grain}</Badge>
          </div>
          <div className="text-[11px] text-muted mt-0.5">{m.name} · from Silver {(m.source_summaries || []).map(s => s.label).join(", ") || `${m.source_ids?.length ?? 0} table(s)`}</div>
          <div className="text-[10.5px] text-muted mt-1 font-mono-tabular">~{num(m.est_rows)} rows · ~{m.est_size_mb} MB · staged {when(m.staged_at)}{m.promoted_at ? ` · promoted ${when(m.promoted_at)}` : ""}</div>
        </div>
        {canEdit && <button data-testid={`gold-model-delete-${m.id}`} onClick={() => onDelete(m.id)} className="w-7 h-7 shrink-0 flex items-center justify-center border border-border hover:border-critical hover:text-critical rounded-sm"><Trash2 size={11} /></button>}
      </div>
      {open && <pre className="border-t border-primary/20 px-3 py-2.5 bg-bg text-[10.5px] font-mono-tabular leading-snug overflow-x-auto whitespace-pre" data-testid={`gold-model-body-${m.id}`}>{m.sql_preview}</pre>}
    </div>
  );
}

function Facts({ onToast }) {
  const { role } = useRole();
  const canEdit = ["it_head", "coo"].includes(role?.id);
  const [models, setModels] = useState(null);
  const [sources, setSources] = useState([]);
  const [studio, setStudio] = useState(false);
  const load = useCallback(() => Promise.all([listGold(), listGoldSources()]).then(([g, s]) => { setModels(g.models || []); setSources(s.sources || []); }).catch(e => onToast(e.message)), [onToast]);
  useEffect(() => { load(); }, [load]);
  const del = async (id) => {
    if (!window.confirm("Delete this Gold fact?")) return;
    try { await removeGold(id); onToast("Gold fact deleted."); load(); } catch (e) { onToast(e.message); }
  };
  return (
    <div className="tile" data-testid="gold-overview">
      <div className="px-4 py-3 border-b border-border flex flex-wrap items-center gap-2">
        <Boxes size={13} className="text-primary" /><span className="kpi-label flex-1">Gold facts · Gold Aggregation Studio</span>
        <Badge status={sources.length ? "info" : "warning"} testid="gold-silver-sources">{sources.length} Silver tables available</Badge>
        {canEdit && <button data-testid="gold-open-studio" onClick={() => setStudio(true)} disabled={!sources.length} className={cn(btn, "h-8 bg-primary text-primary-fg")}><Plus size={12} />New Gold fact</button>}
      </div>
      <div className="p-4 space-y-2">
        <p className="text-[12px] text-muted">Gold facts are composed from <b className="text-fg">Silver tables only</b> — data reaches Silver through Bronze review, so every fact is built on cleaned, approved rows.</p>
        {!models && <div className="text-[12px] text-muted"><Loader2 size={12} className="inline animate-spin mr-1.5" />Loading Gold facts…</div>}
        {models?.length === 0 && <div className="p-6 text-center border border-dashed border-border rounded-sm text-[12px] text-muted" data-testid="gold-empty">
          No Gold facts yet. {sources.length ? "Open the studio to compose one from Silver." : "Promote a Bronze item to Silver first."}</div>}
        {models?.map(m => <Fact key={m.id} m={m} canEdit={canEdit} onDelete={del} />)}
      </div>
      <GoldAggregationStudio open={studio} sources={sources} onClose={() => setStudio(false)} onSaved={(m) => { setStudio(false); onToast(`${m.state === "promoted" ? "Promoted" : "Staged"} “${m.output_table}”.`); load(); }} />
    </div>
  );
}

// Console → Gold: certified Gold tables from Silver, Gold facts from the studio, and the sample Gold catalogue.
export default function GoldTab({ onToast, onChanged }) {
  const [tables, load] = useCurated(onToast);
  const changed = async () => { await load(); onChanged(); };
  return (
    <div className="space-y-6" data-testid="tab-gold">
      <section className="space-y-3">
        <div className="flex items-center gap-2"><Award size={13} className="text-primary" /><span className="kpi-label">Certified Gold tables</span><LayerChip layer="Gold" /></div>
        <CuratedList tables={tables} stage="gold" onChanged={changed} onToast={onToast}
                     empty="No certified Gold tables yet. Certify a Silver table in the Silver tab once its quality is at least 80." />
      </section>
      <GoldPrompt onChanged={changed} />
      <Facts onToast={onToast} />
      <div className="tile px-4 py-3 flex flex-wrap items-center gap-2 text-[12.5px]" data-testid="gold-catalogue-note">
        <Database size={13} className="text-primary" />
        <span className="flex-1">Sample Gold tables keep powering every dashboard. Browse them, with their columns and rows, in the Semantic Data Explorer.</span>
        <Link to="/admin/explorer" data-testid="gold-open-explorer" className="text-primary hover:underline inline-flex items-center gap-1">Open Explorer <ArrowRight size={12} /></Link>
      </div>
    </div>
  );
}
